// M4 contracts: contractors, workers, evidence, permits, access periods (module 09, PB-07-03,
// PB-07-09) and deliveries (PB-09-12 .. PB-09-15). ADR-013. Messages are i18n keys (PD-015).
import { z } from 'zod';

const Uuid = z.uuid();
const trimmed = (max: number) => z.string().trim().min(1, 'v.required').max(max, 'v.tooLong');
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max, 'v.tooLong')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const LocalDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'v.date');
const LocalTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'v.time');
const Key = z.string().trim().regex(/^[a-z][a-z0-9_-]{0,39}$/, 'v.policyKey');
const Version = z.number().int().min(1);
const Reason = trimmed(1000);
const LocalDateTime = z.object({ date: LocalDate, time: LocalTime });
const Email = z
  .string()
  .trim()
  .max(254, 'v.tooLong')
  .transform((v) => (v === '' ? null : v))
  .pipe(z.email('v.email').nullable())
  .nullable()
  .optional();
const Phone = z
  .string()
  .trim()
  .transform((v) => (v === '' ? null : v))
  .pipe(z.string().regex(/^\+?[0-9 ()-]{6,24}$/, 'v.phone').nullable())
  .nullable()
  .optional();

// ------------------------------------------------------------------ requirement configuration (PB-07-09)
const Label = z.object({ en: trimmed(80), ar: z.string().trim().max(80, 'v.tooLong').optional() });
export const EVIDENCE_SUBJECT_VALUES = ['company', 'affiliation', 'person'] as const;
export const EVIDENCE_KIND_VALUES = ['insurance', 'licence', 'noc', 'certification', 'induction', 'training', 'other'] as const;
export const RequirementDocumentSchema = z.object({
  schema: z.literal(1),
  evidenceTypes: z
    .array(
      z.object({
        code: Key,
        label: Label,
        subject: z.enum(EVIDENCE_SUBJECT_VALUES),
        kind: z.enum(EVIDENCE_KIND_VALUES),
        siteSpecific: z.boolean(),
        requiresExpiry: z.boolean(),
        requiresIssuer: z.boolean(),
        maxValidityDays: z.number().int().min(1).max(3660).nullable(),
        expiryLeadDays: z.number().int().min(0).max(365),
        // PB-07-09 (PD-464, PD-465): accepted-issuer allowlist (empty = any issuer) and reviewer assignment.
        acceptedIssuers: z.array(trimmed(200)).max(50).optional(),
        reviewers: z.object({ experiences: z.array(Key).max(10).optional(), groups: z.array(Key).max(10).optional() }).optional(),
      }),
    )
    .max(40),
  rules: z
    .array(
      z.object({
        code: Key,
        label: Label,
        categories: z.array(Key).min(1, 'v.required').max(30),
        securityLevels: z.array(z.enum(['ordinary', 'restricted'])).max(2),
        siteIds: z.array(Uuid).max(100),
        requires: z.array(Key).max(40),
      }),
    )
    .max(40),
  permitTypes: z
    .array(
      z.object({
        code: Key,
        label: Label,
        active: z.boolean(),
        requires: z.array(Key).max(40),
        maxDays: z.number().int().min(1).max(366),
        zoneDependent: z.boolean(),
      }),
    )
    .max(30),
  restrictedRequiresPermit: z.boolean(),
  requireSponsor: z.boolean(),
});
export type RequirementDocumentInput = z.infer<typeof RequirementDocumentSchema>;

export const SaveRequirementDraftRequest = z.object({ expectedVersion: z.number().int().min(0), document: RequirementDocumentSchema, note: optionalText(500) });
export const PublishRequirementRequest = z.object({ expectedVersion: Version });

export interface RequirementSetResponse {
  readonly id: string;
  readonly number: number;
  readonly status: 'draft' | 'published' | 'superseded' | 'discarded';
  readonly document: RequirementDocumentInput;
  readonly note: string | null;
  readonly publishedAt: string | null;
  readonly createdAt: string;
  readonly version: number;
}
export interface RequirementOverviewResponse {
  readonly effective: RequirementSetResponse;
  readonly draft: RequirementSetResponse | null;
  readonly history: readonly RequirementSetResponse[];
  readonly canManage: boolean;
}

// ------------------------------------------------------------------ companies and sponsors (PB-09-01, PB-09-02)
export const SponsorInput = z.object({
  hostId: Uuid,
  backupHostId: Uuid.nullable().optional(),
  department: optionalText(120),
  effectiveFrom: LocalDate,
  effectiveTo: LocalDate.nullable().optional(),
});
export const CreateCompanyRequest = z.object({
  name: trimmed(200),
  kind: z.enum(['contractor', 'vendor']).default('contractor'),
  registrationRef: optionalText(100),
  contactName: optionalText(200),
  contactEmail: Email,
  contactPhone: Phone,
  sponsor: SponsorInput,
});
export const UpdateCompanyRequest = z.object({
  expectedVersion: Version,
  name: trimmed(200).optional(),
  kind: z.enum(['contractor', 'vendor']).optional(),
  registrationRef: optionalText(100),
  contactName: optionalText(200),
  contactEmail: Email,
  contactPhone: Phone,
});
export const ReassignSponsorRequest = SponsorInput.extend({ expectedVersion: Version, reason: Reason });
export const SuspendRequest = z.object({ expectedVersion: Version, reason: Reason, confirmImpact: z.literal(true, 'ctr.v.confirmImpact') });
export const ReinstateRequest = z.object({ expectedVersion: Version, reason: Reason });

export interface SponsorResponse {
  readonly id: string;
  readonly host: { readonly id: string; readonly displayName: string; readonly active: boolean };
  readonly backup: { readonly id: string; readonly displayName: string; readonly active: boolean } | null;
  readonly department: string | null;
  readonly effectiveFrom: string;
  readonly effectiveTo: string | null;
  readonly reason: string | null;
  readonly endedAt: string | null;
  /** True when neither the sponsor nor the backup is active: future work needs reassignment. */
  readonly reassignmentRequired: boolean;
}

export type RequirementStatusDto = 'satisfied' | 'missing' | 'awaiting_file' | 'scan_pending' | 'scan_failed' | 'review_pending' | 'rejected' | 'expires_in_window' | 'not_yet_valid';
export interface RequirementOutcomeDto {
  readonly typeCode: string;
  readonly typeLabel: string;
  readonly subject: 'company' | 'affiliation' | 'person';
  readonly status: RequirementStatusDto;
  readonly evidenceId: string | null;
  readonly validUntil: string | null;
}

export interface SuspensionResponse {
  readonly id: string;
  readonly reason: string;
  readonly suspendedAt: string;
  readonly suspendedBy: string;
  readonly liftedAt: string | null;
  readonly liftReason: string | null;
  readonly impact: SuspensionImpact;
}

export interface CompanySummary {
  readonly id: string;
  readonly name: string;
  readonly kind: 'contractor' | 'vendor';
  readonly registrationRef: string | null;
  readonly status: 'active' | 'suspended' | 'inactive';
  readonly sponsor: SponsorResponse | null;
  readonly workerCount: number;
  /** Company-level requirements of the effective configuration, as of today. */
  readonly evidence: readonly RequirementOutcomeDto[];
  readonly version: number;
}
export interface CompanyDetailResponse extends CompanySummary {
  readonly contactName: string | null;
  readonly contactEmail: string | null;
  readonly contactPhone: string | null;
  readonly sponsors: readonly SponsorResponse[];
  readonly workers: readonly WorkerSummary[];
  readonly formerWorkers: readonly WorkerSummary[];
  readonly suspensions: readonly SuspensionResponse[];
  readonly can: { readonly edit: boolean; readonly suspend: boolean; readonly reinstate: boolean };
}
export interface CompanyListResponse {
  readonly items: readonly CompanySummary[];
  readonly reassignmentRequired: number;
}

// ------------------------------------------------------------------ workers (PB-09-04)
export const CreateWorkerRequest = z.object({
  companyId: Uuid,
  personId: Uuid.optional(),
  givenName: z.string().trim().max(100, 'v.tooLong').optional(),
  familyName: optionalText(100),
  email: Email,
  phone: Phone,
  roleTitle: optionalText(120),
  startsOn: LocalDate,
});
export const MoveWorkerRequest = z.object({ expectedVersion: Version, toCompanyId: Uuid, startsOn: LocalDate, roleTitle: optionalText(120), reason: trimmed(300) });
export const EndAffiliationRequest = z.object({ expectedVersion: Version, endsOn: LocalDate, reason: trimmed(300) });

export interface WorkerSummary {
  readonly personId: string;
  readonly affiliationId: string;
  readonly reference: string;
  readonly displayName: string;
  readonly company: { readonly id: string; readonly name: string; readonly status: 'active' | 'suspended' | 'inactive' };
  readonly roleTitle: string | null;
  readonly startsOn: string;
  readonly endsOn: string | null;
  readonly suspended: boolean;
  readonly version: number;
}
export interface WorkerListResponse {
  readonly items: readonly WorkerSummary[];
}
export interface WorkerDetailResponse {
  readonly person: { readonly id: string; readonly displayName: string; readonly email: string | null; readonly phone: string | null; readonly masked: boolean };
  readonly current: WorkerSummary | null;
  readonly affiliations: readonly WorkerSummary[];
  readonly evidence: readonly EvidenceSummary[];
  readonly suspensions: readonly SuspensionResponse[];
  readonly visits: ReadonlyArray<{ readonly visitId: string; readonly reference: string; readonly siteId: string; readonly title: string | null; readonly startsAt: string | null; readonly planningStatus: string; readonly approvalStatus: string; readonly evidenceStatus: string; readonly presenceStatus: string }>;
  readonly partialScope: boolean;
  readonly can: { readonly manage: boolean; readonly suspend: boolean; readonly reinstate: boolean };
}

// ------------------------------------------------------------------ evidence (PB-09-07, PB-09-09, PB-09-10)
export const EvidenceSubjectInput = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('company'), companyId: Uuid }),
  z.object({ kind: z.literal('affiliation'), affiliationId: Uuid }),
  z.object({ kind: z.literal('person'), personId: Uuid }),
]);
export const CreateEvidenceRequest = z.object({
  typeCode: Key,
  subject: EvidenceSubjectInput,
  siteId: Uuid.nullable().optional(),
  /** 'upload' creates a revision awaiting its file; 'original_sighted' records an original document the submitter has seen. */
  source: z.enum(['upload', 'original_sighted']),
  issuer: optionalText(200),
  referenceNumber: optionalText(100),
  validFrom: LocalDate.nullable().optional(),
  validUntil: LocalDate.nullable().optional(),
  /** The revision this one replaces (PB-09-10: replacement creates a new revision). */
  replacesId: Uuid.nullable().optional(),
});
export const ReviewEvidenceRequest = z.object({
  expectedVersion: Version,
  decision: z.enum(['accept', 'reject']),
  reason: optionalText(1000),
  issuer: optionalText(200),
  referenceNumber: optionalText(100),
  validFrom: LocalDate.nullable().optional(),
  validUntil: LocalDate.nullable().optional(),
});
export const EvidenceListQuery = z.object({
  view: z.enum(['review', 'all', 'expiring']).default('review'),
  subjectKind: z.enum(EVIDENCE_SUBJECT_VALUES).optional(),
  companyId: Uuid.optional(),
  personId: Uuid.optional(),
});

/** Evidence-domain state of a revision for today (states.ts), plus the separate facts. */
export interface EvidenceSummary {
  readonly id: string;
  readonly typeCode: string;
  readonly typeLabel: string;
  readonly subjectKind: 'company' | 'affiliation' | 'person';
  readonly subject: { readonly id: string; readonly label: string; readonly companyName: string | null };
  readonly siteId: string | null;
  readonly revision: number;
  readonly supersedesId: string | null;
  readonly source: 'upload' | 'original_sighted';
  readonly issuer: string | null;
  readonly referenceNumber: string | null;
  readonly validFrom: string | null;
  readonly validUntil: string | null;
  readonly scanStatus: 'no_file' | 'pending' | 'clean' | 'infected' | 'failed' | 'not_applicable';
  readonly reviewStatus: 'pending' | 'accepted' | 'rejected';
  readonly reviewReason: string | null;
  readonly reviewedAt: string | null;
  readonly superseded: boolean;
  readonly state: RequirementStatusDto;
  readonly file: { readonly contentType: string; readonly byteSize: number; readonly previewable: boolean } | null;
  readonly submittedBy: string;
  readonly submittedAt: string;
  readonly version: number;
  /** PD-651 (clamav stream): `rescan` = an explicit, audited retry of a failed or never-run malware check. */
  readonly can: { readonly review: boolean; readonly upload: boolean; readonly replace: boolean; readonly rescan?: boolean };
}
export interface EvidenceListResponse {
  readonly items: readonly EvidenceSummary[];
  /** PD-707: a malware scanner is configured (MALWARE_SCANNER); without one uploads stay unavailable for preview and acceptance. */
  readonly scannerConfigured: boolean;
}

export interface ExpiryTask {
  readonly alertId: string;
  readonly evidenceId: string;
  readonly typeLabel: string;
  readonly subjectLabel: string;
  readonly companyName: string | null;
  readonly expiresOn: string;
  readonly dueOn: string;
  readonly leadDays: number;
  readonly owner: string | null;
  readonly status: 'scheduled' | 'raised' | 'cancelled';
  readonly cancelReason: string | null;
  readonly replacement: { readonly id: string; readonly reviewStatus: string } | null;
  /** PB-09-08: when the worker raised the alert, and who was told (PD-460, PD-461). */
  readonly raisedAt?: string | null;
  readonly notices?: ReadonlyArray<{ readonly recipient: string; readonly kind: 'owner_host' | 'coordinator'; readonly status: 'queued' | 'accepted' | 'failed' | 'blocked'; readonly at: string }>;
}
export interface ExpiryTaskListResponse {
  readonly items: readonly ExpiryTask[];
}

// ------------------------------------------------------------------ work packages (PB-09-03) and access periods (PB-09-05)
export const CreateWorkPackageRequest = z.object({
  companyId: Uuid,
  reference: trimmed(60),
  title: trimmed(200),
  purchaseOrder: optionalText(60),
  commercialNote: optionalText(500),
  startsOn: LocalDate,
  endsOn: LocalDate,
  ownerHostId: Uuid,
});
export const UpdateWorkPackageRequest = z.object({
  expectedVersion: Version,
  title: trimmed(200).optional(),
  purchaseOrder: optionalText(60),
  commercialNote: optionalText(500),
  startsOn: LocalDate.optional(),
  endsOn: LocalDate.optional(),
  ownerHostId: Uuid.optional(),
});
export const CloseWorkPackageRequest = z.object({ expectedVersion: Version, reason: Reason });

export interface WorkPackageResponse {
  readonly id: string;
  readonly siteId: string;
  readonly company: { readonly id: string; readonly name: string };
  readonly reference: string;
  readonly title: string;
  /** Commercial details: null unless the viewer manages contractors at this site (PB-09-03). */
  readonly commercial: { readonly purchaseOrder: string | null; readonly note: string | null } | null;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly owner: { readonly id: string; readonly displayName: string; readonly active: boolean };
  readonly status: 'active' | 'closed';
  readonly closedAt: string | null;
  readonly closeReason: string | null;
  readonly version: number;
}

export const CreateAccessPeriodRequest = z.object({
  workPackageId: Uuid,
  hostId: Uuid,
  categoryCode: Key,
  destinationId: Uuid.nullable().optional(),
  startsOn: LocalDate,
  endsOn: LocalDate,
  weekdays: z.array(z.number().int().min(1).max(7)).min(1, 'v.required').max(7),
  dailyStart: LocalTime,
  dailyEnd: LocalTime,
  exclusions: z.array(LocalDate).max(366).default([]),
  affiliationIds: z.array(Uuid).min(1, 'v.required').max(100),
});
export const MaterialiseRequest = z.object({ expectedVersion: Version, throughDate: LocalDate.optional() });
export const EndAccessPeriodRequest = z.object({ expectedVersion: Version, reason: Reason });

export interface AccessPeriodResponse {
  readonly id: string;
  readonly siteId: string;
  readonly workPackage: { readonly id: string; readonly reference: string; readonly status: 'active' | 'closed'; readonly endsOn: string; readonly version: number };
  readonly workPackageVersion: number;
  readonly host: { readonly id: string; readonly displayName: string };
  readonly categoryCode: string;
  readonly destination: { readonly id: string; readonly name: string } | null;
  readonly startsOn: string;
  readonly endsOn: string;
  readonly weekdays: readonly number[];
  readonly dailyStart: string;
  readonly dailyEnd: string;
  readonly exclusions: readonly string[];
  readonly workers: ReadonlyArray<{ readonly affiliationId: string; readonly displayName: string; readonly current: boolean }>;
  readonly status: 'active' | 'ended';
  readonly endedReason: string | null;
  /** Remaining permitted dates (bounded by the work package as it stands now). */
  readonly remainingDates: readonly string[];
  readonly materialised: ReadonlyArray<{ readonly date: string; readonly visitId: string }>;
  readonly version: number;
}
export interface MaterialiseResponse {
  readonly created: ReadonlyArray<{ readonly date: string; readonly visitId: string }>;
  readonly skipped: ReadonlyArray<{ readonly date: string; readonly reason: 'already_materialised' }>;
  readonly period: AccessPeriodResponse;
}

// ------------------------------------------------------------------ permits (PB-09-06, PB-09-10)
export const CreatePermitRequest = z.object({
  permitTypeCode: Key,
  companyId: Uuid,
  workPackageId: Uuid.nullable().optional(),
  scope: trimmed(1000),
  locationId: Uuid,
  issuer: optionalText(200),
  validFrom: LocalDateTime,
  validUntil: LocalDateTime,
  affiliationIds: z.array(Uuid).min(1, 'v.required').max(50),
});
export const UpdatePermitRequest = z.object({
  expectedVersion: Version,
  scope: trimmed(1000).optional(),
  locationId: Uuid.optional(),
  issuer: optionalText(200),
  validFrom: LocalDateTime.optional(),
  validUntil: LocalDateTime.optional(),
  affiliationIds: z.array(Uuid).min(1, 'v.required').max(50).optional(),
});
export const SubmitPermitRequest = z.object({ expectedVersion: Version });
export const PermitDecisionRequest = z.object({ expectedVersion: Version, decision: z.enum(['approve', 'reject']), reason: optionalText(1000) });
export const WithdrawPermitRequest = z.object({ expectedVersion: Version, reason: Reason });

export interface PermitSummary {
  readonly id: string;
  readonly reference: string;
  readonly siteId: string;
  readonly permitTypeCode: string;
  readonly permitTypeLabel: string;
  readonly company: { readonly id: string; readonly name: string; readonly status: 'active' | 'suspended' | 'inactive' };
  readonly workPackage: { readonly id: string; readonly reference: string } | null;
  readonly scope: string;
  readonly location: { readonly id: string; readonly name: string; readonly restricted: boolean };
  readonly issuer: string | null;
  readonly validFrom: string;
  readonly validUntil: string;
  readonly timeZone: string;
  readonly status: 'draft' | 'submitted' | 'approved' | 'rejected' | 'invalidated' | 'withdrawn';
  readonly invalidatedReason: string | null;
  readonly workers: ReadonlyArray<{ readonly affiliationId: string; readonly personId: string; readonly displayName: string }>;
  /** Readiness of the evidence this permit type needs (separate from the permit decision). */
  readonly evidenceReady: boolean;
  readonly version: number;
}
export interface PermitDetailResponse extends PermitSummary {
  readonly readiness: { readonly ready: boolean; readonly problems: ReadonlyArray<{ readonly code: string; readonly message: string; readonly affiliationId?: string; readonly typeCode?: string }>; readonly outcomes: ReadonlyArray<RequirementOutcomeDto & { readonly affiliationId: string }> };
  readonly decisions: ReadonlyArray<{ readonly id: string; readonly outcome: 'approved' | 'rejected'; readonly reason: string | null; readonly decidedBy: string; readonly decidedAt: string; readonly permitVersion: number }>;
  readonly can: { readonly edit: boolean; readonly submit: boolean; readonly decide: boolean; readonly withdraw: boolean; readonly blockedReason: 'own_submission' | null };
}
export interface PermitListResponse {
  readonly items: readonly PermitSummary[];
}

// ------------------------------------------------------------------ eligibility (PB-07-03) and suspension impact (PB-09-11)
export const EligibilityQuery = z.object({
  personId: Uuid,
  categoryCode: Key,
  startDate: LocalDate,
  endDate: LocalDate,
  destinationId: Uuid.optional(),
});
export interface EligibilityResponse {
  readonly applies: boolean;
  readonly eligible: boolean;
  readonly blockers: ReadonlyArray<{ readonly code: string; readonly message: string; readonly typeCode?: string; readonly status?: string }>;
  readonly requirements: readonly RequirementOutcomeDto[];
  readonly evidenceState: string | null;
  readonly permit: { readonly required: boolean; readonly permitId: string | null; readonly requirements: readonly RequirementOutcomeDto[] };
  readonly requirementSetNumber: number;
}
export interface VisitContractorReadinessResponse {
  readonly visitId: string;
  readonly governed: boolean;
  readonly participants: ReadonlyArray<{
    readonly participationId: string;
    readonly personId: string;
    readonly displayName: string;
    readonly company: string | null;
    /** Independent visit facts, read-only here: approval and presence are never derived from eligibility. */
    readonly approvalStatus: string;
    readonly evidenceStatus: string;
    readonly presenceStatus: string;
    readonly eligibility: EligibilityResponse;
  }>;
}

export interface SuspensionImpact {
  readonly futureVisits: number;
  readonly participations: number;
  readonly observedOnsite: number;
  readonly activeCredentials: number;
  readonly permits: number;
  readonly sites: number;
  readonly items?: ReadonlyArray<{ readonly visitId: string; readonly reference: string; readonly siteId: string; readonly title: string | null; readonly startsAt: string | null; readonly person: string; readonly presenceStatus: string; readonly credentialStatus: string }>;
}
export interface RecoveryTaskResponse {
  readonly id: string;
  readonly siteId: string;
  readonly kind: 'review_visit' | 'revoke_access' | 'occupant_onsite';
  readonly status: 'open' | 'resolved';
  readonly visit: { readonly id: string; readonly reference: string; readonly title: string | null };
  readonly person: string;
  readonly subject: string;
  /** What was observed when the suspension applied. Presence and credential facts are NOT changed by suspension. */
  readonly observed: { readonly presenceStatus: string; readonly credentialStatus: string; readonly approvalStatus: string };
  readonly current: { readonly presenceStatus: string; readonly credentialStatus: string };
  readonly createdAt: string;
  readonly resolvedAt: string | null;
  readonly resolutionNote: string | null;
}
export const ResolveRecoveryTaskRequest = z.object({ note: trimmed(1000) });

// ------------------------------------------------------------------ deliveries (PB-09-12 .. PB-09-15)
export const CreateDeliveryPointRequest = z.object({
  locationId: Uuid,
  priority: z.number().int().min(0).max(1000).default(100),
  capacityAdvisory: z.number().int().min(1).max(10000).nullable().optional(),
  routeNote: optionalText(500),
  contact: optionalText(200),
});
export const UpdateDeliveryPointRequest = z.object({
  expectedVersion: Version,
  status: z.enum(['open', 'closed']).optional(),
  priority: z.number().int().min(0).max(1000).optional(),
  capacityAdvisory: z.number().int().min(1).max(10000).nullable().optional(),
  routeNote: optionalText(500),
  contact: optionalText(200),
});
export interface DeliveryPointResponse {
  readonly id: string;
  readonly siteId: string;
  readonly location: { readonly id: string; readonly name: string; readonly active: boolean };
  readonly status: 'open' | 'closed';
  readonly priority: number;
  readonly capacityAdvisory: number | null;
  readonly routeNote: string | null;
  readonly contact: string | null;
  readonly waiting: number;
  readonly version: number;
}

const Plate = z
  .string()
  .trim()
  .transform((v) => (v === '' ? null : v.toUpperCase()))
  .pipe(z.string().regex(/^[A-Z0-9 -]{1,20}$/, 'ctr.v.plate').nullable())
  .nullable()
  .optional();
export const CreateDeliveryRequest = z.object({
  kind: z.enum(['expected', 'unscheduled']),
  courierCompany: trimmed(200),
  driverName: optionalText(200),
  vehiclePlate: Plate,
  packageReference: optionalText(100),
  packageCount: z.number().int().min(1).max(999).default(1),
  packageNote: optionalText(300),
  recipientHostId: Uuid.nullable().optional(),
  recipientStated: optionalText(200),
  recipientDepartment: optionalText(120),
  escalationOwnerUserId: Uuid.nullable().optional(),
  expectedOn: LocalDate.nullable().optional(),
  deliveryPointId: Uuid.nullable().optional(),
});
export const ArriveDeliveryRequest = z.object({ expectedVersion: Version, driverName: optionalText(200), vehiclePlate: Plate, deliveryPointId: Uuid.nullable().optional() });
export const AssignRecipientRequest = z.object({ expectedVersion: Version, recipientHostId: Uuid });
export const EscalateDeliveryRequest = z.object({ expectedVersion: Version, escalationOwnerUserId: Uuid, reason: Reason });
export const DeliveryHandoverRequest = z.object({
  expectedVersion: Version,
  outcome: z.enum(['handed_over', 'refused']),
  handedToHostId: Uuid.nullable().optional(),
  handedToName: optionalText(200),
  refusalReason: optionalText(500),
});
export const DeliveryDepartureRequest = z.object({ expectedVersion: Version, subject: z.enum(['courier', 'vehicle']) });
export const ResendNoticeRequest = z.object({ expectedVersion: Version });
export const CancelDeliveryRequest = z.object({ expectedVersion: Version, reason: Reason });
export const DeliveryListQuery = z.object({ view: z.enum(['open', 'closed', 'expected', 'mine']).default('open') });

export type DeliveryNoticeStatus = 'queued' | 'accepted' | 'failed' | 'blocked';
export interface DeliveryResponse {
  readonly id: string;
  readonly reference: string;
  readonly siteId: string;
  readonly kind: 'expected' | 'unscheduled';
  readonly courierCompany: string;
  readonly driverName: string | null;
  readonly vehiclePlate: string | null;
  readonly packageReference: string | null;
  readonly packageCount: number;
  readonly packageNote: string | null;
  readonly recipient: { readonly id: string; readonly displayName: string } | null;
  readonly recipientStated: string | null;
  readonly recipientDepartment: string | null;
  readonly escalationOwner: { readonly id: string; readonly displayName: string } | null;
  readonly deliveryPoint: { readonly id: string; readonly name: string; readonly routeNote: string | null } | null;
  readonly expectedOn: string | null;
  readonly arrivedAt: string | null;
  /** Independent facts: the package outcome, the courier, the vehicle and the recipient notice. */
  readonly packageStatus: 'pending' | 'handed_over' | 'refused';
  readonly courierPresence: 'no_observation' | 'observed_onsite' | 'observed_departed';
  readonly vehiclePresence: 'no_observation' | 'observed_onsite' | 'observed_departed' | null;
  /** Latest notice; `channel` added by PB-09-14 (PD-529): email or SMS. */
  readonly notice: { readonly status: DeliveryNoticeStatus; readonly attempts: number; readonly at: string; readonly channel: 'email' | 'sms' } | null;
  readonly handover: { readonly at: string; readonly by: string; readonly to: string | null; readonly refusalReason: string | null } | null;
  readonly courierDepartedAt: string | null;
  readonly vehicleDepartedAt: string | null;
  readonly cancelledAt: string | null;
  readonly timeZone: string;
  readonly version: number;
}
export interface DeliveryDetailResponse extends DeliveryResponse {
  readonly events: ReadonlyArray<{ readonly id: string; readonly kind: string; readonly actor: string | null; readonly at: string; readonly details: Record<string, unknown> }>;
  readonly notices: ReadonlyArray<{ readonly id: string; readonly recipient: string; readonly channel: 'email' | 'sms'; readonly status: DeliveryNoticeStatus; readonly statusReason: string | null; readonly attempts: number; readonly createdAt: string; readonly updatedAt: string }>;
  readonly can: { readonly arrive: boolean; readonly assign: boolean; readonly handover: boolean; readonly depart: boolean; readonly resend: boolean; readonly escalate: boolean; readonly cancel: boolean };
}
export interface DeliveryListResponse {
  readonly items: readonly DeliveryResponse[];
  readonly counts: { readonly open: number; readonly awaitingDeparture: number; readonly unknownRecipient: number; readonly expectedToday: number };
}
